import { ChevronRightIcon } from "@heroicons/react/outline";
import moment from "moment";
import { MeetingHeaderFragmentFragment } from "types/graphql-schema";

import useUiPreferenceCache, {
  MeetingsViewEnum,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppPopover from "@components/popover/app-popover";
import { getUrl, removeCurrentYear } from "@helpers/helpers";

import MeetingDraftPopoverPanel from "./draft-popover-panel";
import PreviousMeetingsPopoverPanel from "./previous-meetings-popover-panel";

const MeetingBreadcrumb = ({
  meeting,
}: {
  meeting?: MeetingHeaderFragmentFragment | null;
}) => {
  const meetingDate = !meeting
    ? null
    : meeting.startDatetime
      ? `${meeting.draft ? "Draft, " : ""}
      ${removeCurrentYear(moment(meeting.startDatetime).format("ddd, ll"))}`
      : meeting.draft
        ? "Draft, unscheduled"
        : "Unscheduled";

  const { uiPreferenceCache } = useUiPreferenceCache();

  return (
    <div className="flex gap-0.5 items-center text-sm text-gray-600 flex-1 min-w-0">
      <Button
        theme={buttonTheme.text}
        size={ButtonSize.medium}
        className="hidden @3xl/layout-header:flex"
        to={
          uiPreferenceCache.meetingsView === MeetingsViewEnum.calendar
            ? "/calendar"
            : "/meetings/group"
        }
      >
        Meetings
      </Button>
      {meeting && (
        <div className="flex gap-0.5 items-center text-sm text-gray-600 min-w-0">
          <ChevronRightIcon className="h-3 w-3 text-gray-500 hidden @3xl/layout-header:flex" />
          <Button
            theme={buttonTheme.text}
            size={ButtonSize.medium}
            to={getUrl({
              meetingGroupId: meeting.meetingGroupId,
              meetingId: meeting.id,
            })}
            className="flex-1 min-w-0"
          >
            <span className="truncate">{meeting.title}</span>
          </Button>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          {meeting.draft ? (
            <AppPopover
              options={{
                className: "z-modal w-96 bg-white rounded-lg shadow-lg",
              }}
              content={() => <MeetingDraftPopoverPanel meeting={meeting} />}
            >
              <AppPopover.Button
                as={Button}
                theme={buttonTheme.text}
                size={ButtonSize.medium}
              >
                {meetingDate}
              </AppPopover.Button>
            </AppPopover>
          ) : meeting.isRecurring ? (
            <PreviousMeetingsPopoverPanel
              key={meeting.meetingGroupId}
              meeting={meeting}
            />
          ) : (
            <Button
              theme={buttonTheme.text}
              size={ButtonSize.medium}
              to={getUrl({
                meetingGroupId: meeting.meetingGroupId,
                meetingId: meeting.id,
              })}
            >
              {meetingDate}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingBreadcrumb;
